import { Link, useLocation } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { useRootSelector } from '@/hooks/selector.hook';

export default function Breadcrumb() {
  const location = useLocation();
  const storedBreadcrumbItems = useRootSelector((state) => state.breadcrumb.items);
  const financialPages: Record<string, string> = {
    '/wallets': 'Wallets',
    '/receiving-accounts': 'Receiving Accounts',
    '/cards': 'Cards',
    '/transactions': 'Transactions',
  };
  const fallbackBreadcrumbItems = financialPages[location.pathname]
    ? [
      { title: 'Financial', link: '/wallets' },
      { title: financialPages[location.pathname] },
    ]
    : location.pathname === '/profile'
      ? [{ title: 'Profile' }]
        : location.pathname === '/notifications'
          ? [{ title: 'Notifications' }]
          : location.pathname === '/' || location.pathname === '/dashboard'
            ? [{ title: 'Dashboard' }]
          : [];
  const breadcrumbItems = (storedBreadcrumbItems.length > 0 ? storedBreadcrumbItems : fallbackBreadcrumbItems)
    .filter((item) => item.title !== 'Financial');

  if (breadcrumbItems.length === 0) return null;

  const firstItem = breadcrumbItems[0];
  const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
  const isDetailPage = breadcrumbItems.length > 1;
  const isProfileBreadcrumb = location.pathname === '/profile' || firstItem.title === 'Profile';
  const FirstItemIcon = isProfileBreadcrumb ? UserRound : null;
  const firstItemIconClassName = 'h-[1.6rem] w-[1.6rem]';

  return (
    <div className="min-w-0 [overflow-wrap:anywhere]">
      <nav className="flex min-w-0 flex-wrap items-center gap-[0.6rem] lg:hidden" aria-label="Breadcrumb mobile">
        {isDetailPage && firstItem.link ? (
          <Link
            to={firstItem.link}
            className="flex items-center gap-[0.6rem] text-disable transition-opacity duration-200 hover:opacity-80"
          >
            {FirstItemIcon ? <FirstItemIcon className={firstItemIconClassName} /> : null}
            <span className="text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] text-disable">
              {firstItem.title}
            </span>
          </Link>
        ) : (
          <div className="flex items-center gap-[0.6rem]">
            {FirstItemIcon ? <FirstItemIcon className={`${firstItemIconClassName} text-disable`} /> : null}
            <span className="text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] text-disable">
              {firstItem.title}
            </span>
          </div>
        )}

        {isDetailPage ? (
          <>
            <span className="text-disable">/</span>
            <span className="text-[1.4rem] font-medium leading-[1.9rem] tracking-[0.007rem] text-[#121212] dark:text-[#CDCDCD]">
              {lastItem.title}
            </span>
          </>
        ) : null}
      </nav>

      <nav className="hidden items-center gap-[0.8rem] text-[1.4rem] lg:flex" aria-label="Breadcrumb">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <div key={`${item.title}-${index}`} className="flex items-center gap-[0.4rem]">
              {item.link && !isLast ? (
                <Link
                  to={item.link}
                  className="flex items-center gap-[0.6rem] font-normal text-[14px] leading-[20px] text-center align-middle px-[1.6rem] py-[0.8rem] text-disable hover:text-disable"
                >
                  {index === 0 && FirstItemIcon ? <FirstItemIcon className={firstItemIconClassName} /> : null}
                  {item.title}
                </Link>
              ) : (
                <span
                  className={`flex items-center gap-[0.6rem] font-normal text-[14px] leading-[20px] text-center align-middle px-[1.6rem] py-[0.8rem] ${isLast ? 'text-[#121212] dark:text-[#CDCDCD]' : 'text-disable'
                    }`}
                >
                  {index === 0 && FirstItemIcon ? <FirstItemIcon className={firstItemIconClassName} /> : null}
                  {item.title}
                </span>
              )}
              {!isLast && (
                <p className="text-disable">/</p>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
